import { useState, useEffect, useContext } from 'react';
import { toast } from 'material-react-toastify';
import AuthApis from '../helper/api.service';
import { UserContext } from '../context/UserContext';
import { postMessage } from '../utils/common.utils';

export const useConsents = (updateLoader, history) => {
	const { 
		setIsFip, 
		isFip, 
		fipEntity,
		consentCache, 
		updateConsentCache, 
		clearConsentCache
	} = useContext(UserContext);

	const [allAccounts, setAllAccounts] = useState([]);
	const [selectedAccounts, setSelectedAccounts] = useState([]);
	const [consentNotFound, setConsentNotFound] = useState(false);
	const [mobileNumber, setMobileNumber] = useState('');
	const [AAHandle, setAAHandle] = useState('');
	const authApis = new AuthApis();

	useEffect(() => {
		// Only use cached data when explicitly coming back from consent details
		const isFromConsentDetails = history?.location?.state?.fromConsentDetails === true;
		
		if (isFromConsentDetails && consentCache.isValid) {
			// Use cached data only when coming back from consent details
			setAllAccounts(consentCache.allAccounts);
			setMobileNumber(consentCache.mobileNumber);
			setAAHandle(consentCache.AAHandle);
			updateLoader(false);
		} else {
			// Clear cache and fetch fresh data for all other cases
			clearConsentCache();
			if (isFip) {
				getFIPConsents();
			} else {
				getLiveConsents();
			}
		}
	}, []);

	const getLiveConsents = async () => {
		updateLoader(true);
		try {
			const res = await authApis.userActiveConsents();
			if (res && res?.status && res?.status === 'SUCCESS') {
				await getConsentDetailsAndEntityInfo(res.userConsents);
			} else if (res && res?.status === 'RECORD-NOT-FOUND') {
				logout();
				setConsentNotFound(true);
				updateLoader(false);
			} else {
				if (res?.message === 'Login Required') {
					history.push('/');
				}
				toast.error(res?.message);
				updateLoader(false);
			}
		} catch (error) {
			toast.error('Error fetching consents');
			updateLoader(false);
		}
	};

	const getFIPConsents = () => {
		updateLoader(true);
		authApis.userFIPConsents().then((res) => {
			if (res && res?.status && res.status === 'SUCCESS') {
				let fipUserConsents = res?.fipUserConsents;		
				if(fipEntity) {
					// Filtering consents based on fipId matching fip_entity from URL params
					fipUserConsents = fipUserConsents.filter(consent => 
						consent.accountDetails?.fipId === fipEntity
					);
				}
				getEntityInfo(fipUserConsents);
			} else if (res && res?.status === 'RECORD-NOT-FOUND') {
				logout();
				setConsentNotFound(true);
				updateLoader(false);
			} else {
				if (res?.message === 'Login Required') {
					history.push('/');
				}
				toast.error(res?.message);
				updateLoader(false);
			}
		});
	};

	const getConsentDetailsAndEntityInfo = async (consents) => {
		let data = [];
		
		for (let i = 0; i < consents.length; i++) {
			const consent = consents[i];
			
			// 1. Get FIU entityInfo for this consent
			const fiuEntityRes = await authApis.entityInfo(consent.consentIntentEntityId, 'FIU');
			if (fiuEntityRes?.status === 'SUCCESS') {
				consent.entityLogoUri = fiuEntityRes.entityLogoUri;
				consent.consentIntentEntityName = fiuEntityRes.entityName;
			}
			
		// 2. Get consentDetails for this consent
		const consentDetailsRes = await authApis.consentDetails(consent.consentIntentId);
		if (consentDetailsRes?.status === 'SUCCESS') {
			consent.consentDetails = consentDetailsRes;
			const accounts = consentDetailsRes.Accounts || [];
			
			// 3. For each account, call entityInfo for its fipId
			for (let j = 0; j < accounts.length; j++) {
				const account = accounts[j];
				const fipEntityRes = await authApis.entityInfo(account.fipId, 'FIP');
				if (fipEntityRes?.status === 'SUCCESS') {
					account.fipName = fipEntityRes.entityName;
					account.fipLogoUri = fipEntityRes.entityLogoUri;
				}
			}
		}	
			data.push(consent);
		}
		setAllAccounts(data);
		getUserInfo(data);
	};

	const getEntityInfo = async (payload) => {
		let data = payload;
		for (let i = 0; i < payload.length; i++) {
			// First call for FIU
			if(payload[i]?.consentIntentEntityId){	
				await authApis
					.entityInfo(payload[i].consentIntentEntityId, 'FIU')
					.then((res) => {
						if (res?.status && res?.status === 'SUCCESS') {
						data[i].entityLogoUri = res.entityLogoUri;
					} else {
						updateLoader(false);
						toast.error(res.message);
					}
				});
			}
			// Second call for FIP
			if(isFip){
				await authApis
				.entityInfo(payload[i].accountDetails.fipId, 'FIP')
				.then((res) => {
					if (res?.status && res?.status === 'SUCCESS') {
						data[i].fipLogoUri = res.entityLogoUri;
						data[i].fipName=res.entityName;
					} else {
						updateLoader(false);
						toast.error(res.message);
					}
				});
			}
			// After both calls, if last item, update state
			if (i === payload.length - 1) {
				setAllAccounts(data);
				getUserInfo(data);
			}
		}
	};

	const getUserInfo = (accountsData) => {
		authApis.getUserDetails((res) => {
			updateLoader(false);
			const mobile = res.UserInfo.mobileNo;
			const aaHandle = res.UserInfo.userId;
			
			setMobileNumber(mobile);
			setAAHandle(aaHandle);
			
			// Update cache with the fetched data
			updateConsentCache({
				allAccounts: accountsData,
				mobileNumber: mobile,
				AAHandle: aaHandle
			});
		});
	};

	const logout = () => {
		// Clear cache on logout
		clearConsentCache();
		authApis.logout(AAHandle).then((res) => {
			postMessage('window', 'CLOSED');
			if (res.status === 'SUCCESS') {
				history.push('/');
			} else {
				toast.error(res.message);
			}
		});
	};

	return {
		allAccounts,
		selectedAccounts,
		setSelectedAccounts,
		consentNotFound,
		mobileNumber,
		AAHandle,
		logout,
		authApis,
	};
};
